// src/pages/Privacy.jsx
import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { MainLayout } from 'components/Layout';

export const Privacy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <MainLayout
      title="Privacy Policy — Northup"
      description="How Northup collects, uses, and protects your data."
    >
      <div className="mx-auto max-w-4xl pb-24">
        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.23, 1, 0.32, 1] }}
          className="mb-10 space-y-4"
        >
          <span className="inline-block rounded-full border border-slate-700/70 bg-slate-900/60 px-4 py-1 text-xs font-semibold uppercase tracking-[0.21em] text-[#9D5666]">
            Legal
          </span>

          <h1 className="text-3xl font-semibold text-slate-50 sm:text-4xl">Privacy Policy</h1>

          <p className="text-sm text-slate-400">Last updated: {new Date().getFullYear()}</p>

          <p className="max-w-2xl text-sm text-slate-400 sm:text-base">
            This Privacy Policy explains how Northup Technologies (&ldquo;Northup&rdquo;,
            &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;) collects, uses, and shares
            information when you use our products and services, including the JustKnow app and any
            related websites, APIs, and tools.
          </p>
        </motion.header>

        {/* Quick nav */}
        <motion.nav
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-10 rounded-2xl border border-slate-800/70 bg-slate-900/50 p-5 text-sm backdrop-blur"
        >
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
            In this policy
          </p>
          <ul className="space-y-2 text-slate-300">
            <li>
              <a href="#info-we-collect" className="hover:text-[#9D5666]">
                1. Information we collect
              </a>
            </li>
            <li>
              <a href="#how-we-use" className="hover:text-[#9D5666]">
                2. How we use your information
              </a>
            </li>
            <li>
              <a href="#sharing" className="hover:text-[#9D5666]">
                3. How we share information
              </a>
            </li>
            <li>
              <a href="#data-rights" className="hover:text-[#9D5666]">
                4. Your data rights & choices
              </a>
            </li>
            <li>
              <a href="#security" className="hover:text-[#9D5666]">
                5. Data security & retention
              </a>
            </li>
            <li>
              <a href="#children" className="hover:text-[#9D5666]">
                6. Children&apos;s privacy
              </a>
            </li>
            <li>
              <a href="#changes" className="hover:text-[#9D5666]">
                7. Changes to this policy
              </a>
            </li>
            <li>
              <a href="#contact" className="hover:text-[#9D5666]">
                8. Contact us
              </a>
            </li>
          </ul>
        </motion.nav>

        <div className="space-y-10 text-sm leading-relaxed text-slate-300">
          {/* 1 */}
          <Section id="info-we-collect" title="1. Information we collect">
            <p>
              We collect information to provide and improve our services. The types of information
              we may collect include:
            </p>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-slate-400">
              <li>
                <span className="font-medium text-slate-200">Account details</span> – such as your
                name, email address, phone number, and account credentials.
              </li>
              <li>
                <span className="font-medium text-slate-200">Profile & preferences</span> – like
                your saved locations, preferred trip fare, communication preferences, and
                interaction settings.
              </li>
              <li>
                <span className="font-medium text-slate-200">Usage information</span> – including
                app interactions, pages viewed, features used, and timestamps.
              </li>
              <li>
                <span className="font-medium text-slate-200">Location data</span> – such as
                approximate or precise location, for features like trip requests, rider matching,
                and nearby business discovery (subject to your device permissions).
              </li>
              <li>
                <span className="font-medium text-slate-200">Device & log data</span> – including IP
                address, device type, operating system, app version, and error logs.
              </li>
              <li>
                <span className="font-medium text-slate-200">Communications</span> – messages you
                send to us, feedback, support requests, and any content submitted through our
                contact or support channels.
              </li>
            </ul>
          </Section>

          {/* 2 */}
          <Section id="how-we-use" title="2. How we use your information">
            <p>We use your information to:</p>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-slate-400">
              <li>Provide, operate, and maintain our apps, APIs, and services.</li>
              <li>
                Facilitate connections between users, businesses, and JustKnow riders for orders and
                trips.
              </li>
              <li>
                Personalize your experience, including suggestions, preferences, and recommended
                content.
              </li>
              <li>
                Communicate with you about updates, security alerts, support, and administrative
                messages.
              </li>
              <li>Monitor performance, debug issues, and develop new features and products.</li>
              <li>Protect our users and platform from fraud, abuse, and security risks.</li>
              <li>Comply with legal obligations and enforce our Terms of Service.</li>
            </ul>
          </Section>

          {/* 3 */}
          <Section id="sharing" title="3. How we share information">
            <p>
              We do not sell your personal data. We may share your information in limited
              circumstances, including:
            </p>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-slate-400">
              <li>
                <span className="font-medium text-slate-200">With business partners</span> – to
                enable order fulfillment, deliveries, and trip requests.
              </li>
              <li>
                <span className="font-medium text-slate-200">With riders</span> – to complete
                deliveries and trips, we may share relevant pickup, drop-off, and contact details.
              </li>
              <li>
                <span className="font-medium text-slate-200">Service providers</span> – such as
                cloud hosting, analytics, and communication providers who help us operate our
                services.
              </li>
              <li>
                <span className="font-medium text-slate-200">Legal & safety</span> – if required by
                law, or to protect the rights, property, or safety of users, the public, or Northup.
              </li>
              <li>
                <span className="font-medium text-slate-200">Business transfers</span> – in
                connection with a merger, acquisition, or restructuring.
              </li>
            </ul>
          </Section>

          {/* 4 */}
          <Section id="data-rights" title="4. Your data rights & choices">
            <p>
              Depending on your location and applicable laws, you may have rights over your personal
              data, such as:
            </p>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-slate-400">
              <li>Accessing the personal data we hold about you.</li>
              <li>Requesting corrections to inaccurate information.</li>
              <li>Requesting deletion of certain information.</li>
              <li>Objecting to or restricting certain processing.</li>
              <li>Withdrawing consent where processing is based on your consent.</li>
            </ul>
            <p className="mt-3 text-slate-400">
              To exercise these rights, contact us using the details in the{' '}
              <a href="#contact" className="text-[#9D5666] hover:text-cyan-300">
                Contact us
              </a>{' '}
              section below. We may ask you to verify your identity before completing your request.
            </p>
          </Section>

          {/* 5 */}
          <Section id="security" title="5. Data security & retention">
            <p>
              We use reasonable technical and organizational measures to protect your data from
              unauthorized access, loss, or misuse. However, no system is completely secure, and we
              cannot guarantee absolute security.
            </p>
            <p className="mt-3">
              We retain your information for as long as necessary to provide our services, comply
              with legal obligations, resolve disputes, and enforce our agreements, or as otherwise
              permitted by law.
            </p>
          </Section>

          {/* 6 */}
          <Section id="children" title="6. Children's privacy">
            <p>
              Our services are not directed to children under the age of 18, and we do not knowingly
              collect personal data from children. If we learn that we have collected data from a
              child, we will take steps to delete such information.
            </p>
          </Section>

          {/* 7 */}
          <Section id="changes" title="7. Changes to this policy">
            <p>
              We may update this Privacy Policy from time to time. When we do, we will revise the
              &ldquo;Last updated&rdquo; date at the top of this page and, where appropriate,
              provide additional notice.
            </p>
          </Section>

          {/* 8 */}
          <Section id="contact" title="8. Contact us">
            <p>
              If you have any questions about this Privacy Policy or our data practices, you can
              contact us at:
            </p>
            <div className="mt-3 space-y-1 text-slate-400">
              <p>Northup Technologies</p>
              <p>Uganda Central Kampala</p>
              <p>Central Division Nakasero II Lourdel</p>
              <p>P.O.Box 150570 Kampala GPO 7 Lourdel</p>
              <p>Kampala, Uganda</p>
              <p className="mt-2">Email: privacy@northup.ug</p>
            </div>
          </Section>

          <p className="pt-6 text-xs text-slate-500">
            This page is for general informational purposes only and does not constitute legal
            advice. You should consult with your own legal counsel to ensure compliance with
            applicable laws.
          </p>
        </div>
      </div>
    </MainLayout>
  );
};

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24">
    <h2 className="mb-3 text-base font-semibold text-slate-100">{title}</h2>
    <div className="space-y-3">{children}</div>
  </section>
);
